// One seed's page (/s/:id): the record, the play button, who made it, its
// stems to audition one by one, and the ways to take it further. Public —
// the core API decides what opens (someone else's private seed is a 404).

import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Pause, Play, Share } from "lucide-react";
import type { SeedApiDetail } from "@shared/api-types.js";
import type { Snapshot } from "@shared/seed-history.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { ApiError, objectUrl } from "@web-shared/lib/api";
import { webApi } from "@web-shared/lib/web-api";
import { relativeTime } from "@web-shared/lib/format";
import { localRoomPath, profilePath, seedPath } from "@/lib/ctas";
import { audioOf } from "@/lib/my-seeds";
import { cleanClipName } from "@/lib/track-name";
import { cn } from "@web-shared/lib/utils";
import { usePlayer, useTrackStatus, type PlayerTrack } from "../player";
import { useSession } from "../session";
import { Avatar } from "../components/Avatar";
import { SeedCover } from "../components/SeedCover";
import { shareSeed } from "../components/share";
import { Waveform } from "../components/Waveform";
import { Remixes } from "../components/Remixes";
import { RemixCredit } from "../components/RemixCredit";
import { RemixCtas, RemixCtaLabel } from "../components/RemixCtas";
import { StartJamButton } from "../components/StartJamButton";
import { LikeButton } from "../components/LikeButton";
import { EditSeedButton } from "../components/EditSeed";
import { FollowButton } from "../components/FollowButton";
import { Comments, commentMarkers, useSeedComments } from "../components/Comments";
import { DawPicker } from "../components/DawPicker";
import { useBottomChrome } from "../chrome";
import { useWaveformBackfill } from "../waveform-backfill";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { clipColor } from "@web-shared/lib/arrangement";
import { planHydration, hasHydratableContent } from "@shared/sync/hydrate.js";
import { fromRoom } from "@web-shared/lib/arrangement";
import { AmbientGlow } from "@/components/AmbientGlow";
import { DownloadButton } from "../components/DownloadButton";
import { SeedLiveJams, useSeedLiveJams } from "../components/SeedLiveJams";
import { download, keyUrl, recordDownload, recordSeedDownload } from "@/lib/download";
import { seedDownloadPlan, seedFiles, seedRoot, trackFiles, type SeedDownloadPlan } from "@/lib/seed-download";
import { seedStemRenderer } from "@/lib/seed-stems";
import { stemFiles } from "@/lib/stems";
import { saveBytes, safeFilename } from "@/lib/download";

/** The seed's downloadable audio, for the rows of its track list. */
const SeedDownloads = createContext<{ seedId: string; plan: SeedDownloadPlan; seed: SeedApiDetail } | null>(null);

/** The whole seed as a zip: the mix, every track rendered whole
 * (`arrangement`), and every clip (`clips`). */
function DownloadSeedButton({ seed, plan, className, iconClassName, children }: { seed: SeedApiDetail; plan: SeedDownloadPlan; className?: string; iconClassName?: string; children?: React.ReactNode }) {
  const seedId = seed.id;
  if (plan.tracks.length === 0 && !plan.masterKey) return null;
  return (
    <DownloadButton
      label={`Download “${plan.title}” (.zip)`}
      why={`Sign in to download “${plan.title}”`}
      className={className}
      iconClassName={iconClassName}
      run={async (onProgress) => {
        const files = seedFiles(plan, keyUrl);
        // The tracks, rendered: a stem-shaped seed's are already its files.
        const stems = plan.stemShaped ? null : seedStemRenderer(seed, keyUrl);
        if (stems) {
          try {
            files.push(...(await stemFiles(stems.lanes, stems.render, stems.totalSec, `${seedRoot(plan)}/arrangement`, onProgress)));
          } finally {
            stems.dispose();
          }
        }
        const saved = await download(plan.title, files, onProgress);
        recordSeedDownload(seedId, "seed_page", saved);
      }}
    >
      {children}
    </DownloadButton>
  );
}

const SEED_STALE_MS = 30 * 60_000;

export function SeedPage() {
  const { id = "" } = useParams();
  const session = useSession();
  const seed = useQuery({
    queryKey: ["web", "seed", id, session?.user.id ?? ""],
    staleTime: SEED_STALE_MS,
    queryFn: () => webApi<SeedApiDetail>(`/seeds/${encodeURIComponent(id)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (seed.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }
  if (seed.isError) {
    const missing = seed.error instanceof ApiError && seed.error.status === 404;
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {missing ? "This seed doesn't exist, or it's private." : "Could not load this seed."}
        <div className="mt-3">
          <Button asChild size="sm" variant="outline">
            <Link to="/discover">Back to Discover</Link>
          </Button>
        </div>
      </div>
    );
  }
  return <SeedView seed={seed.data} mine={session?.user.id === seed.data.producer.id} />;
}

function SeedView({ seed, mine }: { seed: SeedApiDetail; mine: boolean }) {
  const player = usePlayer();
  const handle = seed.producer.handle ?? seed.producer.id;
  const masterUrl = audioOf(seed);
  const master: PlayerTrack | null = masterUrl
    ? {
        id: seed.id,
        title: seed.title,
        url: masterUrl,
        producer: { handle, username: seed.producer.username },
        href: seedPath(seed.id),
        countsFor: seed.id,
      }
    : null;
  const status = useTrackStatus(seed.id);
  const isPlaying = status === "playing";
  useDocumentTitle(`${seed.title} — ${seed.producer.username}`);
  useWaveformBackfill(seed);
  const comments = useSeedComments(seed.id);
  // Tracks from the arrangement snapshot (per-clip audio). One row per LANE,
  // which is what a track is: a lane's clips are the same part down the
  // timeline — an intro, its bounce and that bounce's tail all carry the same
  // name, so listing every clip made eleven rows out of five tracks. The clip
  // that plays for a lane is its LONGEST one, not merely its first: a lane
  // that opens with a one-bar stab and then runs a sixteen-bar loop should
  // audition the loop.
  //
  // Falls back to the legacy `stems` table for seeds uploaded before per-clip
  // audio existed.
  const tracks = useMemo<PlayerTrack[]>(() => {
    const arr = seed.arrangement as Snapshot | null | undefined;
    const clipUrls = seed.clipPreviewUrls ?? seed.clipAudioUrls ?? {};
    if (arr) {
      const result: PlayerTrack[] = [];
      for (const lane of [...(arr.audioTracks ?? []), ...(arr.midiTracks ?? [])]) {
        let best: { audioKey: string; duration: number } | null = null;
        for (const clip of lane.arrangementClips) {
          if (!clip.audioKey || !clipUrls[clip.audioKey]) continue;
          const duration = clip.duration ?? 0;
          if (!best || duration > best.duration) best = { audioKey: clip.audioKey, duration };
        }
        if (!best) continue;
        const name = cleanClipName(lane.name);
        result.push({
          id: `${seed.id}:track:${lane.name}`,
          title: `${seed.title} — ${name}`,
          trackName: name,
          url: clipUrls[best.audioKey]!,
          producer: { handle, username: seed.producer.username },
          href: seedPath(seed.id),
        });
      }
      if (result.length > 0) return result;
    }
    // Legacy fallback: seeds with actual stem rows.
    return seed.stems.map((st) => ({
      id: `${seed.id}:${st.id}`,
      title: `${seed.title} — ${cleanClipName(st.name)}`,
      trackName: cleanClipName(st.name),
      url: st.previewUrl ?? (st.key ? objectUrl(st.key) : st.audioUrl),
      producer: { handle, username: seed.producer.username },
      href: seedPath(seed.id),
    }));
  }, [seed, handle]);
  const meta = [seed.bpm ? `${Math.round(seed.bpm)} BPM` : null, seed.key, seed.genre].filter(Boolean);

  const commentCount = (comments.data?.comments ?? []).length;
  const liveJams = useSeedLiveJams(seed.id);
  /** A track is loaded: the phone's mini player has the bottom of the screen. */
  const docked = player.track !== null;
  const downloads = useMemo(() => ({ seedId: seed.id, plan: seedDownloadPlan(seed), seed }), [seed]);

  return (
    <SeedDownloads.Provider value={downloads}>
    <div>
      {/* ---- Hero ----
          `min-h`, not a fixed `h`: below 1024px the phone layout stacks the
          record, the title and the remix buttons, and a hard 85dvh box with
          `overflow-hidden` cut the buttons off on every short screen. It is
          the tall record that shrinks now, not the page that clips.
          The desktop hero is the same: a column of the content and, under
          it, the waveform — in flow, so in a short or narrow window the hero
          grows (and the record shrinks) instead of the waveform landing on
          top of the record and the like row. */}
      <main className="bleed relative -mt-4 flex min-h-[85dvh] items-start justify-center overflow-x-clip z-10 pt-[6vh] pb-10 lg:flex-col lg:items-center lg:justify-between lg:overflow-clip lg:pt-[2vh] lg:pb-0">
        <AmbientGlow />

        {/* Content wrapper — centered idle, grid on play */}
        <div
          className={cn(
            "relative z-10 w-full mx-auto px-8 py-6 hidden lg:grid lg:flex-1 items-center transition-all duration-700",
            isPlaying
              ? "max-w-[1320px] grid-cols-[minmax(0,1fr)_min(600px,52%)] gap-14"
              : "max-w-[1200px] grid-cols-[minmax(0,1fr)_420px] gap-4",
          )}
          style={{ transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)" }}
        >

          {/* Vinyl — shrinks when sampler is active */}
          <div className="flex justify-center">
            <div className="transition-transform duration-700" style={{ transform: isPlaying ? "scale(0.84)" : "scale(1)", transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)" }}>
              <SeedCover
                seed={{ id: seed.id, title: seed.title, genre: seed.genre, bpm: seed.bpm, key: seed.key, producer: seed.producer.username }}
                spinning={status === "playing"}
                interactive
                playing={status === "playing"}
                remixBadge={seed.remixOf ? "pill" : undefined}
                onPlay={() => master && player.toggle(master)}
                // As wide as the window leaves room for, in both directions:
                // a fixed 480px overlapped the column beside it in a narrow
                // window and the waveform under it in a short one.
                className="w-[clamp(260px,min(40dvw,100dvh-22rem),480px)] 2xl:w-[clamp(260px,min(40dvw,100dvh-22rem),540px)]"
              />
            </div>
          </div>

          {/* Right column. Four tiers, each quieter than the one above it:
              what this is (the title, who made it, its facts on one line),
              where it came from (a remix's credit), what to do with it
              (Remix in — the one loud row on the page), and everything else
              (jam, like, share, download) on a single quiet row. It used to
              be eleven things at four different weights with no order. */}
          <section className="flex min-w-0 flex-col">
            <FitTitle title={seed.title} compact={isPlaying} />

            {/* Who, and the facts — one byline, in both states. */}
            <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-2", isPlaying ? "mt-3" : "mt-4")}>
              <Link to={profilePath(handle)} className="flex min-w-0 items-center gap-2.5 hover:underline">
                <Avatar name={seed.producer.username} url={seed.producer.avatarUrl} className="size-8 rounded-full" />
                <span className="truncate text-base font-semibold">{seed.producer.username}</span>
              </Link>
              {/* A jam's other players, credited on the take they shaped. */}
              {seed.collaborators && seed.collaborators.length > 0 && (
                <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground" title={seed.collaborators.map((c) => c.username).join(", ")}>
                  <span>with</span>
                  <span className="flex -space-x-1.5">
                    {seed.collaborators.map((c) => (
                      <Avatar key={c.id} name={c.username} url={c.avatarUrl} className="size-5 ring-2 ring-background" />
                    ))}
                  </span>
                </span>
              )}
              {!mine && (
                <FollowButton handle={handle} name={seed.producer.username} following={seed.producerFollowedByMe ?? false} quiet />
              )}
            </div>
            <p className="mt-2.5 truncate font-mono text-xs text-muted-foreground">
              {[...meta, tracks.length > 0 ? `${tracks.length} tracks` : null, `v${seed.version}`, relativeTime(seed.createdAt)].filter(Boolean).join(" · ")}
            </p>

            {/* Whose it was: one line and the original a tap away. What
                changed is in the remix tree below, not up here. */}
            {seed.remixOf && <RemixCredit origin={seed.remixOf} self={master} note={null} size="line" className="mt-4" />}

            {/* The way out. */}
            <div className={cn("transition-all duration-600", isPlaying ? "mt-5" : "mt-7")}>
              <RemixCtaLabel>{isPlaying ? "Take it further in" : "Remix in"}</RemixCtaLabel>
              <RemixCtas seedId={seed.id} size="hero" seedTitle={seed.title} producer={seed.producer.username} trackCount={tracks.length} />
            </div>

            {/* Sampler teaser — playing only: blurred preview with CTA overlay */}
            <div className={cn("overflow-hidden transition-all duration-700", isPlaying ? "max-h-[900px] opacity-100 mt-5" : "max-h-0 opacity-0 pointer-events-none")} style={{ transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)" }}>
              <SamplerTeaser
                seedId={seed.id}
                arrangement={seed.arrangement as Snapshot | null}
              />
            </div>

            {/* Anyone jamming on it right now. */}
            <SeedLiveJams jams={liveJams} className="mt-5" />

            {/* Everything else, quietly: the jam on the left, the three
                things you do TO a seed on the right. */}
            <div className="mt-5 flex items-center gap-1 border-t border-white/[0.07] pt-4 text-muted-foreground">
              <StartJamButton seedId={seed.id} size="bar" label={liveJams.length > 0 ? "Start your own jam" : "Start a jam"} />
              <span className="flex-1" />
              <LikeButton seedId={seed.id} title={seed.title} liked={seed.likedByMe ?? false} count={seed.likeCount ?? 0} size="lg" />
              <Button variant="ghost" size="icon" className="size-9" onClick={() => void shareSeed(seed)} title="Share" aria-label="Share"><Share className="size-4" /></Button>
              <DownloadSeedButton seed={seed} plan={downloads.plan} className="size-9 hover:bg-white/5" />
              {mine && <EditSeedButton seed={seed} />}
            </div>
          </section>
        </div>

        {/* ---- Mobile layout ---- */}
        <div className="relative z-10 flex flex-col items-center w-full lg:hidden">
          {/* Large centered disc */}
          <div className="flex justify-center pt-2">
            <SeedCover
              seed={{ id: seed.id, title: seed.title, genre: seed.genre, bpm: seed.bpm, key: seed.key, producer: seed.producer.username }}
              spinning={isPlaying}
              interactive
              playing={isPlaying}
              remixBadge={seed.remixOf ? "pill" : undefined}
              onPlay={() => master && player.toggle(master)}
              className="w-[min(250px,56dvw)]"
            />
          </div>

          {/* The desktop hero's tiers, stacked for a thumb: what it is (title,
              one byline, its facts on one line), where it came from (one
              line), the waveform, who is jamming on it, and one quiet row
              for the rest. Remix — the loud one — is the bar docked at the
              bottom of the screen (MobileBottomCta). */}
          <div className="mt-6 flex w-full flex-col items-center px-5 text-center">
            <h1 className="text-3xl font-black tracking-tight">{seed.title}</h1>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2">
              <Link to={profilePath(handle)} className="flex min-w-0 items-center gap-2 hover:underline">
                <Avatar name={seed.producer.username} url={seed.producer.avatarUrl} className="size-7 rounded-full" />
                <span className="truncate text-sm font-semibold">{seed.producer.username}</span>
              </Link>
              {seed.collaborators && seed.collaborators.length > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground" title={seed.collaborators.map((c) => c.username).join(", ")}>
                  <span>with</span>
                  <span className="flex -space-x-1.5">
                    {seed.collaborators.map((c) => (
                      <Avatar key={c.id} name={c.username} url={c.avatarUrl} className="size-5 ring-2 ring-background" />
                    ))}
                  </span>
                </span>
              )}
              {!mine && (
                <FollowButton handle={handle} name={seed.producer.username} following={seed.producerFollowedByMe ?? false} quiet />
              )}
            </div>
            <p className="mt-2.5 max-w-full truncate font-mono text-[11px] text-muted-foreground">
              {[...meta, tracks.length > 0 ? `${tracks.length} tracks` : null, `v${seed.version}`, relativeTime(seed.createdAt)].filter(Boolean).join(" · ")}
            </p>
            {seed.remixOf && (
              <RemixCredit origin={seed.remixOf} self={master} note={null} size="line" className="mt-3 max-w-full justify-center text-[13px]" />
            )}
          </div>

          {/* Waveform with time labels */}
          <div className="mt-5 w-full px-5">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] text-muted-foreground/60 w-8">0:00</span>
              <div className="flex-1">
                <Waveform
                  track={master}
                  waveform={seed.waveform}
                  markers={commentMarkers(comments.data?.comments ?? [], seed.durationMs)}
                  className="h-10"
                />
              </div>
              <span className="font-mono text-[10px] text-muted-foreground/60 w-8 text-right">
                {seed.durationMs ? `${Math.floor(seed.durationMs / 60000)}:${String(Math.floor((seed.durationMs % 60000) / 1000)).padStart(2, "0")}` : ""}
              </span>
            </div>
          </div>

          {/* Mobile tabs: Stems / Remixes / Comments */}
          <div className="mt-4 w-full px-5">
            <SeedLiveJams jams={liveJams} compact />
            {/* What you do TO a seed, quietly. Start a jam is in the docked
                bar with Remix and Play here; the mini player takes that
                bar's place once something is playing, so the jam moves up
                here rather than disappearing. */}
            <div className={cn("flex items-center gap-0.5 border-t border-white/[0.07] pt-3 text-muted-foreground", !docked && "justify-center gap-3", liveJams.length > 0 && "mt-3")}>
              {docked && (
                <>
                  <StartJamButton seedId={seed.id} size="bar" label={liveJams.length > 0 ? "Start your own" : "Start a jam"} />
                  <span className="flex-1" />
                </>
              )}
              <LikeButton seedId={seed.id} title={seed.title} liked={seed.likedByMe ?? false} count={seed.likeCount ?? 0} size="lg" />
              <Button variant="ghost" size="icon" className="size-10" onClick={() => void shareSeed(seed)} title="Share" aria-label="Share"><Share className="size-4" /></Button>
              <DownloadSeedButton seed={seed} plan={downloads.plan} className="size-10" />
              {mine && <EditSeedButton seed={seed} />}
            </div>
          </div>

          <MobileSeedTabs
            tracks={tracks}
            seed={seed}
            master={master}
          />
        </div>

        {/* Waveform — desktop only, at bottom of hero */}
        <div
          className={cn(
            "relative w-full max-w-[1100px] shrink-0 px-6 z-30 hidden lg:flex flex-col gap-2 overflow-hidden transition-all duration-700",
            isPlaying ? "max-h-0 translate-y-6 opacity-0 pb-0" : "max-h-24 opacity-100 pb-3",
          )}
          style={{ transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)" }}
        >
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-muted-foreground w-10">0:00</span>
            <div className="relative flex-grow">
              <Waveform
                track={master}
                waveform={seed.waveform}
                markers={commentMarkers(comments.data?.comments ?? [], seed.durationMs)}
                className="h-10"
              />
            </div>
            <span className="font-mono text-xs text-muted-foreground w-10 text-right">
              {seed.durationMs ? `${Math.floor(seed.durationMs / 60000)}:${String(Math.floor((seed.durationMs % 60000) / 1000)).padStart(2, "0")}` : ""}
            </span>
          </div>
          <a href="#details" className="self-center flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground hover:text-white">
            {tracks.length} tracks &middot; {commentCount} comments &middot; v{seed.version}
            <span className="animate-bounce text-primary">&darr;</span>
          </a>
        </div>
      </main>

      {/* ---- Mobile bottom CTA bar ---- */}
      <MobileBottomCta
        seedId={seed.id}
        seedTitle={seed.title}
      />

      {/* ---- Details section (desktop) ---- */}
      <section id="details" className="bleed relative z-20 pt-12 pb-32 hidden lg:block">
        <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

        <div className="max-w-[1300px] mx-auto px-6 grid grid-cols-12 gap-12">
          {/* Left: tracks + comments */}
          <div className="col-span-7 flex flex-col gap-8">
            {tracks.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-hairline bg-raised shadow-xl">
                <div className="flex items-center justify-between border-b border-hairline bg-white/[0.02] p-6">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    Tracks <span className="text-muted-foreground text-sm font-normal">{tracks.length}</span>
                  </h2>
                  <DownloadSeedButton seed={seed} plan={downloads.plan} className="h-8 px-2.5 text-xs font-medium" iconClassName="size-3.5">
                    Download all
                  </DownloadSeedButton>
                </div>
                <TrackList tracks={tracks} />
              </div>
            )}
            <Comments seed={seed} master={master} />
          </div>

          {/* Right: remix tree + CTA */}
          <div className="col-span-5 flex flex-col gap-8">
            <Remixes seedId={seed.id} />

            <div className="bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 rounded-2xl p-6 flex flex-col gap-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {seed.remixCount
                    ? `${seed.remixCount} remix${seed.remixCount !== 1 ? "es" : ""} so far`
                    : "Nobody else has remixed this yet"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">Your version shows up in the list above, credited to you.</p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Remix in</span>
              <RemixCtas seedId={seed.id} seedTitle={seed.title} producer={seed.producer.username} trackCount={tracks.length} />
              <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-4">
                <span className="text-sm text-muted-foreground">Or play it live with others</span>
                <StartJamButton seedId={seed.id} size="bar" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </SeedDownloads.Provider>
  );
}

/** Mobile tabs below the seed hero: Stems, Remixes, Comments. */
const MOBILE_TABS = ["stems", "remixes", "comments"] as const;
type MobileTab = typeof MOBILE_TABS[number];

function MobileSeedTabs({
  tracks,
  seed,
  master,
}: {
  tracks: PlayerTrack[];
  seed: SeedApiDetail;
  master: PlayerTrack | null;
}) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: MobileTab = MOBILE_TABS.includes(raw as MobileTab) ? (raw as MobileTab) : "stems";
  const setTab = (t: MobileTab) => {
    const next = new URLSearchParams(params);
    if (t === "stems") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: true });
  };
  return (
    <div className="mt-5 w-full px-5 pb-32">
      {/* Tab bar */}
      <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-raised border border-strong text-xs font-semibold">
        {([
          ["stems", `Stems`],
          ["remixes", `Remixes ${seed.remixCount || ""}`],
          ["comments", "Comments"],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "h-8 rounded-lg transition-colors",
              tab === id ? "bg-[#262630] text-white" : "text-muted-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="mt-3">
        {tab === "stems" && <TrackList tracks={tracks} />}
        {tab === "remixes" && <Remixes seedId={seed.id} inline />}
        {tab === "comments" && <Comments seed={seed} master={master} />}
      </div>
    </div>
  );
}


/** Title at 92px by default. Only shrinks if it exceeds 2 lines at that size. */
/** Blurred, non-interactive preview of the pad grid with a CTA to open the editor. */
function SamplerTeaser({ seedId, arrangement: snapshot }: { seedId: string; arrangement: Snapshot | null }) {
  const navigate = useNavigate();
  const room = useMemo(() => {
    if (!snapshot || !hasHydratableContent(snapshot)) return null;
    let id = 0;
    return planHydration(snapshot, (k) => `t_${k[0]}_${++id}`, () => true)?.room ?? null;
  }, [snapshot]);

  const lanes = useMemo(() => {
    if (!room) return [];
    return fromRoom(room.tracks, room.clips).lanes;
  }, [room]);

  if (lanes.length === 0) return null;

  // Dedupe clips per lane for display.
  const cols = lanes.map((l) => {
    const seen = new Set<string>();
    return l.clips.filter((c) => {
      const k = `${c.name}:${Math.round(c.duration * 2)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  });
  const rows = Math.min(3, Math.max(1, ...cols.map((c) => c.length)));

  return (
    <div className="relative overflow-hidden rounded-2xl border border-hairline bg-raised/90 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
      {/* Blurred pad grid */}
      <div className="blur-[6px] opacity-60 pointer-events-none select-none">
        <div className="grid gap-2.5" style={{ gridTemplateColumns: `repeat(${lanes.length}, minmax(0, 1fr))` }}>
          {/* Track headers */}
          {lanes.map((lane, i) => {
            const color = clipColor(lane.clips[0]?.color ?? 0, i);
            return (
              <div key={lane.id} className="h-8 rounded-lg flex items-center gap-2 px-3 text-xs font-bold border border-white/10">
                <span className="size-2 rounded-full" style={{ backgroundColor: color }} />
                <span className="truncate">{lane.name}</span>
              </div>
            );
          })}
          {/* Fake pads */}
          {Array.from({ length: rows }, (_, row) =>
            lanes.map((lane, laneIdx) => {
              const clip = cols[laneIdx]?.[row];
              const color = clipColor(clip?.color ?? 0, laneIdx);
              return (
                <div
                  key={`${lane.id}:${row}`}
                  className="h-[40px] rounded-lg border p-2"
                  style={{
                    background: row === 0 ? `${color}38` : `${color}12`,
                    borderColor: row === 0 ? `${color}90` : "rgba(255,255,255,0.06)",
                  }}
                >
                  <span className="text-[11px] font-semibold text-foreground/70 truncate block">
                    {clip?.name || ""}
                  </span>
                </div>
              );
            }),
          )}
        </div>
      </div>

      {/* CTA overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => navigate(localRoomPath(seedId))}
          className="h-10 px-5 rounded-lg bg-white/10 backdrop-blur-md text-foreground/90 font-semibold text-xs border border-white/15 flex items-center gap-2 hover:bg-white/20 transition-all"
        >
          <svg className="size-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" viewBox="0 0 24 24">
            <rect x={3} y={5} width={18} height={14} rx={2} /><path d="M7 10h6M7 14h10" />
          </svg>
          Play with the stems
        </button>
        <span className="text-xs text-muted-foreground">Open the editor to remix this seed in your browser</span>
      </div>
    </div>
  );
}

function FitTitle({ title, compact }: { title: string; compact: boolean }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [fontSize, setFontSize] = useState(92);
  const [measured, setMeasured] = useState(false);
  const BASE = 92;
  const LINE_H = 0.9;
  const MIN = 36;

  useLayoutEffect(() => {
    if (compact) return;
    const el = ref.current;
    if (!el) return;
    // Disable transitions so measurements are instant.
    el.style.transition = "none";
    el.style.fontSize = `${BASE}px`;
    el.style.lineHeight = String(LINE_H);
    // Force layout reflow so the browser computes the new size.
    el.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions
    const maxH = Math.ceil(BASE * LINE_H * 2) + 12;
    if (el.scrollHeight <= maxH) {
      setFontSize(BASE);
      el.style.transition = "";
      setMeasured(true);
      return;
    }
    // Exceeds 2 lines — shrink until it fits.
    let size = BASE - 2;
    while (size > MIN) {
      el.style.fontSize = `${size}px`;
      el.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions
      const h = Math.ceil(size * LINE_H * 2) + 12;
      if (el.scrollHeight <= h) break;
      size -= 2;
    }
    setFontSize(size);
    el.style.transition = "";
    setMeasured(true);
  }, [title, compact]);

  return (
    <h1
      ref={ref}
      className="min-w-0 font-black tracking-tighter"
      style={{
        fontSize: compact ? 34 : fontSize,
        lineHeight: compact ? 1 : LINE_H,
        letterSpacing: "-0.04em",
        // Only animate after first measurement — prevents flash of wrong size.
        transition: measured ? "font-size 600ms cubic-bezier(0.2, 0.8, 0.2, 1), line-height 600ms cubic-bezier(0.2, 0.8, 0.2, 1)" : "none",
      }}
    >
      {title}
    </h1>
  );
}

const VISIBLE_TRACKS = 5;

function TrackList({ tracks }: { tracks: PlayerTrack[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? tracks : tracks.slice(0, VISIBLE_TRACKS);
  const hidden = tracks.length - VISIBLE_TRACKS;
  return (
    <div className="flex flex-col">
      {visible.map((t) => (
        <TrackRow key={t.id} name={t.trackName ?? t.title} track={t} queue={tracks} />
      ))}
      {!expanded && hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="px-6 py-3 text-xs font-medium text-primary hover:text-white transition-colors text-center"
        >
          Show all {tracks.length} tracks
        </button>
      )}
    </div>
  );
}

function TrackRow({ name, track, queue }: { name: string; track: PlayerTrack; queue: PlayerTrack[] }) {
  const player = usePlayer();
  const status = useTrackStatus(track.id);
  const seedDownloads = useContext(SeedDownloads);
  const stem = seedDownloads?.plan.tracks.find((t) => t.id === track.id);
  return (
    <div className="group flex items-center gap-4 border-b border-white/[0.02] px-6 py-4 transition-colors hover:bg-white/[0.02]">
      <button
        type="button"
        onClick={() => player.toggle(track, queue)}
        aria-label={status === "playing" ? `Pause ${name}` : `Play ${name}`}
        className={cn(
          "flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-all",
          status
            ? "border-primary/30 bg-primary/10 text-primary"
            : "border-white/10 bg-white/5 text-foreground/70 group-hover:border-primary/30 group-hover:bg-primary/10 group-hover:text-primary",
        )}
      >
        {status === "loading" ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : status === "playing" ? (
          <Pause className="size-3.5" />
        ) : (
          <Play className="size-3.5" />
        )}
      </button>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{name}</span>
      {stem && seedDownloads && (
        <DownloadButton
          label={`Download ${name} — the whole track`}
          why={`Sign in to download “${name}”`}
          className="size-10"
          run={async (onProgress) => {
            // The track as it plays on the timeline, bar 1 to the end. A
            // stem-shaped seed's file already is that; a track that cannot
            // be rendered falls back to its clips.
            const title = seedDownloads.plan.title;
            const stems = seedDownloads.plan.stemShaped ? null : seedStemRenderer(seedDownloads.seed, keyUrl);
            const lane = stems?.lanes.find((l) => l.name === stem.name);
            if (stems && lane) {
              try {
                const [file] = await stemFiles([lane], stems.render, stems.totalSec, "", onProgress);
                if (file?.bytes) {
                  saveBytes(`${safeFilename(`${title} - ${name}`)}.wav`, file.bytes, "audio/wav");
                  recordDownload("stem_download", "seed_page", seedDownloads.seedId, 1);
                  return;
                }
              } finally {
                stems.dispose();
              }
            }
            const saved = await download(`${title} - ${name}`, trackFiles(stem, keyUrl), onProgress);
            recordDownload("stem_download", "seed_page", seedDownloads.seedId, saved);
          }}
        />
      )}
    </div>
  );
}

/** The fixed bottom bar on mobile with "Remix in your DAW" + "Play here".
 *  Hides when the mini player is active to avoid stacking three bars. */
function MobileBottomCta({
  seedId,
  seedTitle,
}: {
  seedId: string;
  seedTitle: string;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const { track } = usePlayer();
  const playerActive = track !== null;
  // Docks on the same chrome the mini player would — the two are mutually
  // exclusive (this returns null once a track is loaded), but they must agree
  // on where the bottom of the screen is.
  const chrome = useBottomChrome();

  if (playerActive) return showPicker ? (
    <DawPicker seedId={seedId} seedTitle={seedTitle} onClose={() => setShowPicker(false)} />
  ) : null;

  return (
    <>
      <div className="fixed inset-x-0 z-40 px-4 pt-4 pb-2 bg-gradient-to-t from-background via-background to-transparent lg:hidden" style={{ bottom: chrome }}>
        <div className="flex justify-center gap-2">
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center gap-1.5"
          >
            Remix
            <svg className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></svg>
          </button>
          <Link
            to={localRoomPath(seedId)}
            className="h-10 px-3 rounded-xl border border-white/15 flex items-center text-xs font-semibold"
          >
            Play here
          </Link>
          <StartJamButton seedId={seedId} size="dock" />
        </div>
      </div>
      {showPicker && (
        <DawPicker
          seedId={seedId}
          seedTitle={seedTitle}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}
